import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import { GhlService } from '../../../services/ghl/ghl.service';
import { PeopleOutlineOutlined } from '@mui/icons-material';

interface Option {
  id: string;
  icon: React.ReactNode;
  label: string;
  description: string;
}

const options: Option[] = [
  {
    id: 'solo',
    icon: <PersonOutlineRoundedIcon sx={{ fontSize: 20, color: '#374151' }} />,
    label: 'Solo creator',
    description: 'Coach, consultant, or individual educator',
  },
  {
    id: 'small_business',
    icon: <PeopleOutlineOutlined sx={{ fontSize: 20, color: '#374151' }} />,
    label: 'Small business',
    description: 'Growing team running webinars for leads & sales',
  },
  {
    id: 'agency',
    icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 20, color: '#374151' }} />,
    label: 'Agency',
    description: 'Managing webinars for multiple clients',
  },
  {
    id: 'enterprise',
    icon: <GridViewOutlinedIcon sx={{ fontSize: 20, color: '#374151' }} />,
    label: 'Enterprise',
    description: 'Large-scale events with advanced security',
  },
];

interface RoleSelectionStepProps {
  email: string;
  initialSelected: string;
  onComplete: (role: string) => void;
  onSkip: () => void;
  onBack: () => void;
  ghlContactId: string;
}

export default function RoleSelectionStep({ email, initialSelected, onComplete, onSkip, onBack, ghlContactId }: RoleSelectionStepProps) {
  const [selected, setSelected] = useState<string>(initialSelected || '');

  useEffect(() => {
    setSelected(initialSelected || '');
  }, [initialSelected]);

  return (
    <Box>
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          color: '#111827',
          fontSize: { xs: '1.25rem', sm: '1.9rem' },
          fontWeight: 700,
          letterSpacing: '-0.01em',
          mb: 0.75,
        }}
      >
        What best describes you?
      </Typography>
      <Typography sx={{ textAlign: 'center', color: '#6B7280', fontSize: {xs: '0.82rem', sm: '0.92rem'}, mb: 3 }}>
        This helps us tailor your workspace
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
        {options.map((opt) => {
          const isSelected = selected === opt.id;
          return (
            <Box
              key={opt.id}
              onClick={() => setSelected(opt.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: '10px',
                border: isSelected ? '1px solid #18181B' : '1px solid #E5E7EB',
                bgcolor: isSelected ? '#F3F3F3' : '#FFFFFF',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
                '&:hover': {
                  borderColor: isSelected ? '#18181B' : '#C4C6F0',
                },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  "& svg": {
                    width: '100%',
                    height: '100%',
                  },
                }}
              >
                {opt.icon}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: {xs: '0.88rem', sm: '0.92rem'}, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ fontSize: {xs: '0.74rem', sm: '0.78rem'}, color: '#6B7280', lineHeight: 1.4, mt: 0.2 }}>
                  {opt.description}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: isSelected ? 'none' : '1.5px solid #D1D5DB',
                  bgcolor: isSelected ? '#111827' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}
              >
                {isSelected && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={!selected}
        onClick={async () => {
          onComplete(selected);
          let ghlContactIdToUpdate = "";
          if (!ghlContactId) {
            ghlContactIdToUpdate = await GhlService.getContactByEmail(email).then((data: any) => data?.id);
          } else {
            ghlContactIdToUpdate = ghlContactId;
          }
          GhlService.updateContactFields({ contactId: ghlContactIdToUpdate, fields: [{ key: "roles", value: selected }] });
        }}
        sx={{
          textTransform: 'none',
          borderRadius: '10px',
          py: 1,
          fontSize: '0.92rem',
          fontWeight: 600,
          color: '#FFFFFF',
          bgcolor: '#4058fb',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#3442bd', boxShadow: 'none' },
          '&.Mui-disabled': {
            bgcolor: '#4058fb',
            color: '#C5C7D0',
            opacity: 0.6,
            cursor: 'not-allowed',
            boxShadow: 'none'
          },
        }}
      >
        Continue
      </Button>

      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="text"
          onClick={onBack}
          sx={{ textTransform: 'none', color: '#6066E9', fontSize: '0.9rem', fontWeight: 500 }}
        >
          Back
        </Button>
        <Button
          variant="text"
          onClick={onSkip}
          sx={{ textTransform: 'none', color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}
        >
          Skip
        </Button>
      </Box>
    </Box>
  );
}
